import { X } from 'lucide-react';
import type { Badge } from '../../types/badges';
import { useStudentBadgeData } from '../../hooks/useStudentBadgeData';
import { XPHistoryModal } from './XPHistoryModal';
import { useState, useContext } from 'react';
import { UserContext } from '../../contexts/UserContext';
import mediaService from '../../services/mediaService';

interface BadgeModalProps {
  badge: Badge;
  isOpen: boolean;
  onClose: () => void;
}

export const BadgeModal = ({ badge, isOpen, onClose }: BadgeModalProps) => {
  if (!isOpen) return null;

  const { userProfile } = useContext(UserContext);
  const [showHistory, setShowHistory] = useState(false);
  const { activities } = useStudentBadgeData();

  const getLevelProgress = (level: keyof Badge['levelThresholds']) => {
    const threshold = badge.levelThresholds[level];
    const percentage = Math.min((badge.currentXP / threshold) * 100, 100);
    return {
      threshold,
      percentage,
      isUnlocked: badge.currentXP >= threshold
    };
  };

  const levels = Object.entries(badge.levelThresholds)
    .sort((a, b) => a[1] - b[1])
    .map(([key]) => ({
      name: key.charAt(0).toUpperCase() + key.slice(1),
      key: key as keyof Badge['levelThresholds'],
      color: badge.color
    }));

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X size={24} />
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <div className="flex items-center justify-center mb-6">                  <div 
                    className="w-24 h-24 flex items-center justify-center text-5xl rounded-full animate-badge-glow"
                    style={{
                      boxShadow: `0 0 20px ${badge.color}80`
                    }}
                  >
                      <img
                        src={(badge.currentLevel !== 'none' ? badge.levelImages[badge.currentLevel] : Object.values(badge.levelImages)[0])?.startsWith('https://')
                          ? (badge.currentLevel !== 'none' ? badge.levelImages[badge.currentLevel] : Object.values(badge.levelImages)[0])
                          : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.currentLevel !== 'none' ? badge.levelImages[badge.currentLevel] : Object.values(badge.levelImages)[0]))}
                      alt={`${badge.name} ${badge.currentLevel} badge`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>

                <h3 className="text-2xl leading-6 font-bold text-gray-900 text-center mb-2">
                  {badge.name}
                </h3>
                <p className="text-sm text-gray-500 text-center mb-6">
                  {badge.description}
                </p>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Current XP</span>
                    <span className="text-lg font-bold text-indigo-600">{badge.currentXP} XP</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                      Category: <span className="font-medium">{badge.category}</span>
                    </div>
                    {badge.currentXP > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowHistory(true);
                        }}
                        className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center font-medium"
                      >
                        XP History
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-gray-900">Level Progress</h4>
                  {levels.map((level) => {
                    const progress = getLevelProgress(level.key);
                    return (
                      <div key={level.key} className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <img 
                              src={badge.levelImages[level.key]?.startsWith('https://')
                                ? badge.levelImages[level.key]
                                : mediaService.getDirectUrl(userProfile?.orgId || '', String(badge.levelImages[level.key]))}
                              alt={`${level.name} badge`}
                              className="w-6 h-6 object-contain"
                            />
                            <span className="font-medium" style={{ color: level.color }}>
                            {level.name}
                            </span>
                          </div>
                          <span className="text-gray-600">
                            {progress.isUnlocked ? 'Unlocked!' : `${progress.threshold} XP required`}
                          </span>
                        </div>
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full transition-all duration-500"
                            style={{
                              width: `${progress.percentage}%`,
                              backgroundColor: level.color
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <XPHistoryModal
        isOpen={showHistory}
        onClose={() => setShowHistory(false)}
        activities={activities.filter(a => a.reason.includes(badge.category))}
        badgeName={badge.name}
      />
    </div>
  );
};